import type { IPathResult } from '../../astar/astarInterface'
import type { PImage } from '../../engine/aliases'
import type { IDeadish, IEntity } from '../../entities/entitiesUtils'
import { entitiesUtils } from '../../entities/entitiesUtils-sidecar'
import { assert } from '../../lib/assert-sidecar'
import type { IIJ } from '../../lib/distanceUtil'
import { l } from '../../lib/imports'
import { mathUtils } from '../../lib/mathUtils-sidecar'
import { createId } from '../../main/common'
import { common } from '../../main/common-sidecar'
import { sprites } from '../../sprites/sprites-sidecar'
import { spritesUtil } from '../../sprites/spritesUtil-sidecar'
import { voyageMessageCrewCarry } from './multi/voyageMessageCrewCarry-sidecar'
import {
  EVoyageCrewDamageType,
  IMessageCrewHurt,
} from './multi/voyageMessageCrewHurt'
import { voyageMessageCrewHurt } from './multi/voyageMessageCrewHurt-sidecar'

import { voyageMessageCrewHurtEnergy } from './multi/voyageMessageCrewHurtEnergy-sidecar'
import { voyageMessageMoney } from './multi/voyageMessageMoney-sidecar'
import { messageWriter } from '../../engine/messageWriter-sidecar'
import { voyageBodies } from './voyageBodies-sidecar'
import type { IVoyageCarryable } from './voyageCarryable'
import { voyageCarryable } from './voyageCarryable-sidecar'
import { EVoyageCarryableType } from './voyageCarryableData'
import { voyageColors } from './voyageColors-sidecar'
import { voyageCrew } from './voyageCrew-sidecar'
import type { IVoyageCrewWeaponState } from './voyageCrewWeaponData'
import { voyageCrewWeaponData } from './voyageCrewWeaponData-sidecar'
import type { IVoyageCrewAlien } from './voyageCrew_alien'
import { voyageCrew_alien } from './voyageCrew_alien-sidecar'
import type { IVoyageCrewAnimal } from './voyageCrew_animal'
import { voyageCrew_animal } from './voyageCrew_animal-sidecar'
import type { IVoyageCrewCrew } from './voyageCrew_crew'
import { voyageCrew_crew } from './voyageCrew_crew-sidecar'
import type { IVoyageCrewFire } from './voyageCrew_fire'
import { voyageCrew_fire } from './voyageCrew_fire-sidecar'
import { voyageCrew_fireable } from './voyageCrew_fireable-sidecar'
import type { IVoyageCrewHullBreach } from './voyageCrew_hullBreach'
import { voyageCrew_hullBreach } from './voyageCrew_hullBreach-sidecar'
import { voyageCrew_movable } from './voyageCrew_movable-sidecar'
import { voyageFakeInfo } from './voyageFakeInfo-sidecar'
import { voyageFloatingText } from './voyageFloatingText-sidecar'
import { voyageGameStatus } from './voyageGameStatus-sidecar'
import { voyageLayers } from './voyageLayers-sidecar'
import { voyageParticles } from './voyageParticles-sidecar'
import { voyageProgressBars } from './voyageProgressBars-sidecar'
import { voyageSounds } from './voyageSounds-sidecar'
import { voyageTilemap } from './voyageTilemap-sidecar'
import type { IVoyageTilemapPart } from './voyageTilemapPart'
import { voyageWebsockets } from './voyageWebsockets-sidecar'
import type { IMessageCrewHurtEnergy } from './multi/voyageMessageCrewHurtEnergy'

export enum EVoyageCrewType {
  Default = -1,
  Null = 0,
  Crew = 1,
  Fire = 2,
  Alien = 3,
  HullBreach = 4,
  Animal = 5,
}

export interface IVoyageCrew extends IEntity, IDeadish {
  type: EVoyageCrewType
  image: PImage
  x: number
  y: number

  sx: number
  sy: number
  tx: number
  ty: number
  isLerping: boolean
  lerpTime: number
  lerpTimeMax: number

  i: number
  j: number
  name: string
  title: string
  info1: string
  team: number
  path: IPathResult<IVoyageTilemapPart> | null
  pathIdx: number
  moveTimer: number
  footstepTimer: number
  hitpoints: number
  fireState: IVoyageCrewFire | null
  breachState: IVoyageCrewHullBreach | null
  crewState: IVoyageCrewCrew | null
  alienState: IVoyageCrewAlien | null
  animalState: IVoyageCrewAnimal | null
  weaponState: IVoyageCrewWeaponState | null
  // carryable: IVoyageCarryable | null
  items: IVoyageCarryable[]
  tile: IVoyageTilemapPart | null

  requestedInfo: boolean
  energy: number
  energyTimer: number
  sleepyEffectTimer: number
  passedOutTimer: number
  isPassedOut: boolean

  isFlipped: boolean
}

const objs: IVoyageCrew[] = []
export function getAll() {
  return objs
}

export function findById(id: number) {
  return entitiesUtils.findById(id, objs)
}

export function findByIdSafe(id: number) {
  return entitiesUtils.findByIdSafe(id, objs)
}

export function getAt(iij: IIJ, filter?: (f: IVoyageCrew) => boolean) {
  for (let i = 0; i < objs.length; i++) {
    let c = objs[i]
    if (iij.i === c.i && iij.j === c.j) {
      if (!filter || filter(c)) {
        return c
      }
    }
  }
  return null
}

export function create(type: EVoyageCrewType, team: number): IVoyageCrew {
  let isHost = voyageWebsockets.getIsHost()

  let scale = 1
  let frame = sprites.voyTileCrew[0]
  if (type === EVoyageCrewType.Fire) {
    frame = sprites.voyTileFire[0]
    scale = 0.5
  }
  if (type === EVoyageCrewType.HullBreach) {
    frame = sprites.voyTileChecker2[0]
    scale = 0.5
  }
  if (type === EVoyageCrewType.Animal) {
    frame = sprites.voyAnimalPoodle[0]
    scale = 0.25
  }
  if (type === EVoyageCrewType.Alien) {
    frame = sprites.voyInkMonsterAlien[0]
    scale = 0.25
  }
  let image = spritesUtil.createImage(voyageLayers.getLayers().shipCrew, frame)
  image.scale = scale

  let info1 = ''
  let title = ''

  let id = createId()
  let name = '' + id
  if (type === EVoyageCrewType.Fire) {
    name = 'Fire ' + name
    image.tint = voyageColors.yellow
  }
  if (type === EVoyageCrewType.HullBreach) {
    name = 'Hull Breach ' + name
    image.tint = voyageColors.damage
  }
  if (type === EVoyageCrewType.Crew) {
    // name = 'Crew ' + name
    if (isHost) {
      // name = voyageFakeInfo.genFakeName()
      info1 = voyageFakeInfo.genFakeLike()
      title = voyageFakeInfo.genFakeTitle()
    } else {
      // name = 'Fetching bio...'
    }
  }
  if (type === EVoyageCrewType.Alien) {
    name = 'Alien ' + name
    image.tint = voyageColors.green
  }
  let obj: IVoyageCrew = {
    id,
    idLoaded: -1,
    isDead: false,
    eType: 'voy-misc',
    image,
    team,
    type,
    x: 0,
    y: 0,
    sx: 0,
    sy: 0,
    tx: 0,
    ty: 0,
    isLerping: false,
    lerpTime: 0,
    lerpTimeMax: 0,
    i: -1,
    j: -1,
    name,
    info1,
    title,
    path: null,
    pathIdx: 0,
    moveTimer: 0,
    footstepTimer: 0,
    hitpoints: 100,
    fireState: null,
    crewState: null,
    breachState: null,
    alienState: null,
    animalState: null,
    weaponState: null,

    // carryable: null,
    items: [],

    requestedInfo: isHost,

    // Todo: move to crew?
    energy: 0,
    energyTimer: 0,
    sleepyEffectTimer: 0,
    passedOutTimer: 0,
    isPassedOut: false,
    tile: null,
    isFlipped: false,
  }
  objs.push(obj)

  if (type === EVoyageCrewType.Fire) {
    obj.fireState = voyageCrew_fire.create()
    obj.hitpoints = 1
  }

  if (type === EVoyageCrewType.HullBreach) {
    obj.breachState = voyageCrew_hullBreach.create()
    obj.hitpoints = 10
  }
  if (type === EVoyageCrewType.Crew) {
    obj.crewState = voyageCrew_crew.create()
    obj.weaponState = voyageCrewWeaponData.create(
      voyageCrewWeaponData.getCrewBlaster(),
    )
    obj.hitpoints = 100

    obj.image.visible = false

    // obj.carryable = voyageCarryable.create(EVoyageCarryableType.Coffee)
  }
  if (type === EVoyageCrewType.Alien) {
    obj.alienState = voyageCrew_alien.create()
    obj.weaponState = voyageCrewWeaponData.create(
      voyageCrewWeaponData.getAlienBlaster(),
    )
    obj.hitpoints = 100
    obj.title = 'Alien Scum'
  }

  if (type === EVoyageCrewType.Animal) {
    obj.animalState = voyageCrew_animal.create()
    obj.hitpoints = 100
    obj.title = 'Indie'
  }

  return obj
}

export function setTile(obj: IVoyageCrew, tile: IVoyageTilemapPart | null) {
  if (obj.tile) {
    if (obj.tile.system && obj.tile.system.data.onLeave) {
      obj.tile.system.data.onLeave(obj)
    }

    l.remove(obj.tile.crew, obj)
  }
  obj.tile = tile
  if (obj.tile) {
    obj.tile.crew.push(obj)
  }
}

export function moveToIJ(obj: IVoyageCrew, iij: IIJ) {
  obj.i = iij.i
  obj.j = iij.j
  let t = voyageTilemap.getSafe(iij.i, iij.j)
  setTile(obj, t)
  if (t) {
    moveToXY(obj, t.x, t.y)

    if (obj.type === EVoyageCrewType.Fire) {
      let tCur = voyageTilemap.getSafeIJ(obj)
      if (tCur && tCur.fire === obj) {
        tCur.fire = null
      }
      t.fire = obj
    }

    if (obj.type === EVoyageCrewType.HullBreach) {
      let tCur = voyageTilemap.getSafeIJ(obj)
      if (tCur && tCur.breach === obj) {
        tCur.breach = null
      }
      t.breach = obj
    }

    if (obj.type === EVoyageCrewType.Crew) {
      if (t.system) {
        if (t.system.data.onEnter) {
          t.system.data.onEnter(obj, voyageCrew_movable.isFinalMove(obj))
        }
      }
    }
  }
}

export function moveToXY(obj: IVoyageCrew, x: number, y: number) {
  let e = 1.5
  if (x - obj.x < -e) {
    obj.isFlipped = true
  }
  if (x - obj.x > e) {
    obj.isFlipped = false
  }

  obj.x = x
  obj.y = y
  obj.image.x = x
  obj.image.y = y

  if (obj.crewState && obj.crewState.body) {
    voyageBodies.moveToXY(obj.crewState.body, x, y, obj.isFlipped)
  }
  if (obj.crewState && obj.crewState.progressBar) {
    voyageProgressBars.moveTo(obj.crewState.progressBar, obj.x, obj.y + 8)
  }

  if (obj.items.length > 0) {
    l.forEach(obj.items, (c) => {
      let flipMult = obj.isFlipped ? -1 : 1
      voyageCarryable.moveToXY(
        c,
        x + c.data.carryOx * flipMult,
        y + c.data.carryOy,
        obj.isFlipped,
      )
    })
  }

  if (obj.type === EVoyageCrewType.Animal) {
    obj.image.flipX = obj.isFlipped
  }
}

export function kill(obj: IVoyageCrew) {
  obj.isDead = true

  setTile(obj, null)

  if (obj.type === EVoyageCrewType.Fire) {
    let tCur = voyageTilemap.getSafeIJ(obj)
    if (tCur && tCur.fire === obj) {
      tCur.fire = null
    }
  }

  if (obj.type === EVoyageCrewType.HullBreach) {
    let tCur = voyageTilemap.getSafeIJ(obj)
    if (tCur && tCur.breach === obj) {
      tCur.breach = null
    }
  }
}
export function killAll() {
  l.forEach(objs, (c) => {
    kill(c)
  })
  removeDead()
}

export function updateAll() {
  l.forEach(objs, (c) => {
    update(c)
  })
  removeDead()
}
export function removeDead() {
  entitiesUtils.removeDead(objs, (c) => {
    c.image.destroy()
    setTile(c, null)

    if (c.crewState && c.crewState.body) {
      voyageBodies.kill(c.crewState.body)
    }

    if (c.items.length > 0) {
      l.forEach(c.items, (d) => {
        voyageCarryable.kill(d)
      })
    }
  })
}

export function isHolding(obj: IVoyageCrew, type: EVoyageCarryableType) {
  // if (obj.items.length === 0) {
  //   return false
  // }
  for (let i = 0; i < obj.items.length; i++) {
    if (obj.items[i].type === type) {
      return true
    }
  }
  return false
}

export function getFirstDroppableItem(obj: IVoyageCrew) {
  for (let i = 0; i < obj.items.length; i++) {
    if (obj.items[i].type !== EVoyageCarryableType.Coffee) {
      return obj.items[i]
    }
  }
  return null
}

export function getItemSafe(obj: IVoyageCrew, type: EVoyageCarryableType) {
  for (let i = 0; i < obj.items.length; i++) {
    if (obj.items[i].type === type) {
      return obj.items[i]
    }
  }
  return null
}

function update(obj: IVoyageCrew) {
  let ds = common.getUpdateDeltaScaled()

  if (obj.type === EVoyageCrewType.Fire) {
    voyageCrew_fire.update(obj)
  }
  if (obj.type === EVoyageCrewType.HullBreach) {
    voyageCrew_hullBreach.update(obj)
  }

  if (obj.isDead) {
    return
  }
  if (obj.isDying) {
    kill(obj)
  }

  if (obj.type === EVoyageCrewType.Crew) {
    voyageCrew_movable.update(obj)
    voyageCrew_fireable.update(obj)
    voyageCrew_crew.update(obj)
  }

  if (obj.type === EVoyageCrewType.Alien) {
    voyageCrew_movable.update(obj)
    voyageCrew_fireable.update(obj)
    voyageCrew_alien.update(obj)
    // console.log('update aliens')
  }

  if (obj.type === EVoyageCrewType.Animal) {
    voyageCrew_movable.update(obj)
    voyageCrew_animal.update(obj)
  }
}

export function say(obj: IVoyageCrew, text: string) {
  if (!text) {
    return
  }
  if (obj.isPassedOut) {
    return
  }
  voyageFloatingText.create(obj.image.x, obj.image.y - 16, text)
}

export function hurt(
  obj: IVoyageCrew,
  damage: number,
  reason: EVoyageCrewDamageType,
  hurtByCrewId: number,
) {
  if (voyageWebsockets.getIsHost()) {
    voyageMessageCrewHurt.sendMessageAndImmediatelyExecute({
      crewId: obj.id,
      byCrewId: hurtByCrewId,
      newHitpoints: obj.hitpoints - damage,
      type: reason,
    })

    // obj.hitpoints -= damage
    // if (obj.hitpoints <= 0) {
    //   makeDeadish(obj, reason)
    // }
  }
}

export function networkHurt(msg: IMessageCrewHurt) {
  let obj = findById(msg.crewId)
  assert.notNil(obj)

  if (obj.type === EVoyageCrewType.Crew || obj.type === EVoyageCrewType.Alien) {
    if (msg.newHitpoints > obj.hitpoints) {
      // Heal
      let healer = findByIdSafe(msg.byCrewId)
      if (healer && healer.crewState) {
        healer.crewState.statHeals += msg.newHitpoints - obj.hitpoints
      }
    } else {
      // Damage
      let damager = findByIdSafe(msg.byCrewId)
      if (damager && damager.crewState) {
        damager.crewState.statDamageGiven += -(msg.newHitpoints - obj.hitpoints)
      }
    }
  }
  obj.hitpoints = msg.newHitpoints
  if (obj.hitpoints <= 0 && !obj.isDying && !obj.isDead) {
    if (msg.type === EVoyageCrewDamageType.Teleport) {
      voyageParticles.createMultiple('teleport', obj.x, obj.y + 16, 18)
      voyageCrew.kill(obj)
    } else {
      if (obj.type === EVoyageCrewType.Alien) {
        if (voyageWebsockets.getIsHost()) {
          voyageMessageMoney.sendMessageAndImmediatelyExecute({
            money: voyageGameStatus.get().money + 25,
          })
        }
      }

      makeDeadish(obj, msg.type, msg.byCrewId)
    }
  }
}

export const maxEnergy = 500
export function hurtEnergy(obj: IVoyageCrew, energyDamage: number) {
  if (voyageWebsockets.getIsHost()) {
    let newEnergyPoints = obj.energy - energyDamage
    newEnergyPoints = mathUtils.clamp(newEnergyPoints, 0, maxEnergy)

    voyageMessageCrewHurtEnergy.sendMessageAndImmediatelyExecute({
      crewId: obj.id,
      newEnergyPoints: newEnergyPoints,
    })
  }
}

export function networkHurtEnergy(msg: IMessageCrewHurtEnergy) {
  let obj = findById(msg.crewId)
  assert.notNil(obj)
  obj.energy = msg.newEnergyPoints

  obj.passedOutTimer = 0
  obj.isPassedOut = obj.energy === 0
}

export function makeDeadish(
  obj: IVoyageCrew,
  damageType: EVoyageCrewDamageType,
  byCrewId: number,
) {
  if (!obj.isDying) {
    if (obj.type === EVoyageCrewType.Alien) {
      voyageSounds.playDeathAlien()
      voyageGameStatus.addAlienDead()

      if (voyageWebsockets.getIsHost()) {
        // Create a corpse here
        voyageMessageCrewCarry.sendCreateMessageAndImmediatelyExecute({
          type: EVoyageCarryableType.AlienCorpse,
          i: obj.i,
          j: obj.j,
        })
      }

      voyageCrew.moveToIJ(obj, { i: 0, j: 0 })
    }
    if (obj.type === EVoyageCrewType.Crew) {
      voyageSounds.playDeathCrewmember()

      let reason = voyageMessageCrewHurt.getDamageReason(damageType)
      voyageGameStatus.addDeadLog(obj.name + ' (' + obj.title + ') ' + reason)

      if (voyageWebsockets.getIsHost()) {
        // Create a corpse here
        voyageMessageCrewCarry.sendCreateMessageAndImmediatelyExecute({
          type: EVoyageCarryableType.CrewCorpse,
          i: obj.i,
          j: obj.j,
          crewId: obj.id,
        })
      }

      // voyageCrew.moveToIJ(obj, { i: 0, j: 0 })
    }
    obj.image.tint = voyageColors.g2
    obj.isDying = true

    let killer = voyageCrew.findByIdSafe(byCrewId)
    if (killer && killer.crewState) {
      if (
        obj.type === EVoyageCrewType.Alien ||
        obj.type === EVoyageCrewType.Crew
      ) {
        killer.crewState.statKills++
      }

      if (obj.type === EVoyageCrewType.Fire) {
        killer.crewState.statFiresFought++
      }

      if (obj.type === EVoyageCrewType.HullBreach) {
        killer.crewState.statRepairs++
      }
    }
  }
}
